import { useMemo } from "react";

import { useGetPolls } from "../../../api/insights";
import { ChartFrame, QuestionList } from "../charts";
import SectionShell, { Stat, formatNum, formatPct } from "../SectionShell";

const POLL_TYPE_LABEL = {
  singleChoice: "Single choice",
  multipleChoice: "Multiple choice",
  text: "Text",
};

const toItem = (poll, isFollowUp) => ({
  id: poll.pollId,
  title: poll.title ?? "(untitled poll)",
  meta: `${POLL_TYPE_LABEL[poll.type] ?? poll.type ?? "—"} · ${formatNum(
    poll.totalResponses
  )} responses · ${formatNum(poll.uniqueRespondents)} unique`,
  kind: poll.breakdown?.kind === "text" ? "text" : "choice",
  options: poll.breakdown?.options,
  answers: poll.breakdown?.answers,
  isFollowUp,
});

function PollsAnalysis({ eventId }) {
  const { data, isPending, isError, error } = useGetPolls(eventId);
  const items = useMemo(
    () =>
      (data?.polls ?? []).flatMap((p) => [
        toItem(p, false),
        ...(p.followUps ?? []).map((fu) => toItem(fu, true)),
      ]),
    [data]
  );

  return (
    <SectionShell
      id="pollsAnalysis"
      title="Polls"
      description="Live poll participation across the event."
      isPending={isPending}
      isError={isError}
      error={error}
      eventId={eventId}
      analysisKey="pollsAnalysis"
      stats={
        <>
          <Stat
            part="totalPolls"
            label="Total polls"
            value={formatNum(data?.totalPolls)}
          />
          <Stat
            part="totalResponses"
            label="Total responses"
            value={formatNum(data?.totalResponses)}
          />
          <Stat
            part="participants"
            label="Participants"
            value={formatNum(data?.participants)}
          />
          <Stat
            part="participationRate"
            label="Participation rate"
            value={formatPct(data?.participationRate)}
          />
          <Stat
            part="avgResponsesPerPoll"
            label="Avg responses / poll"
            value={
              Number.isFinite(Number(data?.avgResponsesPerPoll))
                ? Number(data.avgResponsesPerPoll).toFixed(2)
                : "—"
            }
          />
        </>
      }
      lead={
        <ChartFrame
          part="perPollBreakdown"
          title="Per-poll results"
          hasData={items.length > 0}
        >
          <QuestionList items={items} />
        </ChartFrame>
      }
    />
  );
}

export default PollsAnalysis;
